import type {
    DockFlyoutDataModel,
    DockFlyoutFilterSection,
    DockFlyoutFiltersState,
    DockFlyoutListItem,
    LoadDockFlyoutDataParams,
    ItemFilterConfig,
    ItemQueryConfig,
    ItemScopeConfig,
    ItemSortConfig,
} from "kci-design-lib";
import {
    buildOwnershipScopes,
    mapActionIdsToActions,
    matchesFilters,
    matchesQuery,
    matchesScope,
    mockDelay,
    paginateItems,
    sortItems,
} from "kci-design-lib";

export interface DockFlyoutItemsResponse<T> {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
}

export interface DockFlyoutFilterOption {
    key: string;
    label: string;
}

export interface DockFlyoutFilterOptionsResponse {
    ownership: DockFlyoutFilterOption[];
    category: DockFlyoutFilterOption[];
}

export interface AgentDockFlyoutItem {
    id: string;
    isAggregate?: boolean;
    agentId: string;
    pageId?: string;
    name: string;
    description: string;
    icon: string;
    status: "idle" | "running";
    ownership: "shared" | "personal" | "global" | "member-firm";
    lastRun: string;
    createdAt: string;
    runCount: number;
    successRate: number;
    avgDuration: number;
    createdBy: {
        id: string;
        name: string;
    };
    tags: string[];
    categories: string[];
    activitySummary: {
        actionRequired: number;
        pending: number;
        inProgress: number;
        notStarted: number;
    };
    optionsMenu: {
        show: boolean;
        actions: Array<"pin" | "share" | "edit" | "rename" | "delete" | "download">;
    };
}

export const MOCK_AGENT_DOCK_FLYOUT_ITEMS_RESPONSE: DockFlyoutItemsResponse<AgentDockFlyoutItem> = {
    items: [
        {
            id: "agent-000",
            isAggregate: true,
            agentId: "AG-0000",
            pageId: "agent-AG-0000",
            name: "All Agents Activity",
            description: "Combined view across every agent",
            icon: "smart_toy",
            status: "idle",
            ownership: "shared",
            lastRun: "2026-05-31T09:15:00Z",
            createdAt: "2026-01-20T08:00:00Z",
            runCount: 120,
            successRate: 98.2,
            avgDuration: 95,
            createdBy: {
                id: "system",
                name: "System",
            },
            tags: ["all-agents", "aggregate"],
            categories: ["general-purpose", "financial-statements", "knowledge"],
            activitySummary: {
                actionRequired: 0,
                pending: 0,
                inProgress: 0,
                notStarted: 0,
            },
            optionsMenu: {
                show: false,
                actions: [],
            },
        },
        {
            id: "agent-001",
            agentId: "AG-1234",
            pageId: "agent-AG-1234",
            name: "Rebate Analyzer",
            description: "Analyzes rebate-related workflows and pending tasks",
            icon: "smart_toy",
            status: "idle",
            ownership: "global",
            lastRun: "2026-05-31T11:30:00Z",
            createdAt: "2026-04-05T10:00:00Z",
            runCount: 45,
            successRate: 96.5,
            avgDuration: 120,
            createdBy: {
                id: "user-001",
                name: "Sarah Chen",
            },
            tags: ["rebate", "analysis"],
            categories: ["financial-statements"],
            activitySummary: {
                actionRequired: 1,
                pending: 2,
                inProgress: 5,
                notStarted: 1,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
        {
            id: "agent-002",
            agentId: "AG-1235",
            pageId: "agent-AG-1235",
            name: "Revenue Cutoff Activity",
            description: "Tracks and reviews revenue cutoff related tasks",
            icon: "smart_toy",
            status: "idle",
            ownership: "global",
            lastRun: "2026-05-30T13:45:00Z",
            createdAt: "2026-04-18T09:30:00Z",
            runCount: 39,
            successRate: 95.1,
            avgDuration: 110,
            createdBy: {
                id: "user-002",
                name: "Michael Lee",
            },
            tags: ["revenue", "cutoff"],
            categories: ["financial-statements"],
            activitySummary: {
                actionRequired: 4,
                pending: 3,
                inProgress: 1,
                notStarted: 5,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
        {
            id: "agent-003",
            agentId: "AG-1236",
            pageId: "agent-AG-1236",
            name: "Cross-Engagement Risk Scanner Activity",
            description: "Scans cross-engagement risks and exceptions",
            icon: "smart_toy",
            status: "running",
            ownership: "shared",
            lastRun: "2026-06-01T07:40:00Z",
            createdAt: "2026-05-01T12:00:00Z",
            runCount: 28,
            successRate: 94.3,
            avgDuration: 140,
            createdBy: {
                id: "user-003",
                name: "David Miller",
            },
            tags: ["risk", "cross-engagement"],
            categories: ["financial-statements", "knowledge"],
            activitySummary: {
                actionRequired: 4,
                pending: 0,
                inProgress: 3,
                notStarted: 2,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
        {
            id: "agent-004",
            agentId: "AG-1237",
            pageId: "agent-AG-1237",
            name: "Smart Automation",
            description: "Automates repetitive review workflows",
            icon: "smart_toy",
            status: "idle",
            ownership: "personal",
            lastRun: "2026-05-25T06:30:00Z",
            createdAt: "2026-05-20T11:05:00Z",
            runCount: 16,
            successRate: 97.8,
            avgDuration: 80,
            createdBy: {
                id: "user-004",
                name: "Emily Johnson",
            },
            tags: ["automation", "workflow"],
            categories: ["general-purpose"],
            activitySummary: {
                actionRequired: 0,
                pending: 5,
                inProgress: 2,
                notStarted: 0,
            },
            optionsMenu: {
                show: true,
                actions: ["pin", "edit", "share", "delete"],
            },
        },
        {
            id: "agent-005",
            agentId: "AG-1238",
            pageId: "agent-AG-1238",
            name: "AR Confirmation Tracker",
            description: "Tracks account receivable confirmation progress",
            icon: "smart_toy",
            status: "idle",
            ownership: "shared",
            lastRun: "2026-05-28T16:05:00Z",
            createdAt: "2026-03-28T07:15:00Z",
            runCount: 22,
            successRate: 93.7,
            avgDuration: 130,
            createdBy: {
                id: "user-005",
                name: "Rachel Adams",
            },
            tags: ["ar", "confirmation"],
            categories: ["financial-statements"],
            activitySummary: {
                actionRequired: 4,
                pending: 0,
                inProgress: 0,
                notStarted: 5,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
        {
            id: "agent-006",
            agentId: "AG-1239",
            pageId: "agent-AG-1239",
            name: "Journal Entry Assistant",
            description: "Assists with journal entry review and validation",
            icon: "smart_toy",
            status: "running",
            ownership: "personal",
            lastRun: "2026-05-27T10:00:00Z",
            createdAt: "2026-05-09T10:20:00Z",
            runCount: 31,
            successRate: 96.9,
            avgDuration: 100,
            createdBy: {
                id: "user-006",
                name: "Priya Nair",
            },
            tags: ["journal-entry", "assistant"],
            categories: ["general-purpose", "knowledge"],
            activitySummary: {
                actionRequired: 1,
                pending: 2,
                inProgress: 3,
                notStarted: 0,
            },
            optionsMenu: {
                show: true,
                actions: ["pin", "edit", "share", "delete"],
            },
        },
        {
            id: "agent-007",
            agentId: "AG-1240",
            pageId: "agent-AG-1240",
            name: "Lease Review Assistant",
            description: "Reviews lease-related knowledge and actions",
            icon: "smart_toy",
            status: "idle",
            ownership: "member-firm",
            lastRun: "2026-05-26T15:25:00Z",
            createdAt: "2026-05-14T14:30:00Z",
            runCount: 19,
            successRate: 95.8,
            avgDuration: 90,
            createdBy: {
                id: "user-007",
                name: "Kevin Brown",
            },
            tags: ["lease", "review"],
            categories: ["knowledge"],
            activitySummary: {
                actionRequired: 0,
                pending: 3,
                inProgress: 1,
                notStarted: 0,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
        {
            id: "agent-008",
            agentId: "AG-1241",
            pageId: "agent-AG-1241",
            name: "Depreciation Review Agent",
            description: "Analyzes depreciation review activities and exceptions",
            icon: "smart_toy",
            status: "idle",
            ownership: "shared",
            lastRun: "2026-05-29T09:00:00Z",
            createdAt: "2026-05-12T08:00:00Z",
            runCount: 27,
            successRate: 94.9,
            avgDuration: 125,
            createdBy: {
                id: "user-008",
                name: "Olivia Wilson",
            },
            tags: ["depreciation", "review"],
            categories: ["financial-statements"],
            activitySummary: {
                actionRequired: 5,
                pending: 2,
                inProgress: 0,
                notStarted: 0,
            },
            optionsMenu: {
                show: true,
                actions: ["pin"],
            },
        },
    ],
    total: 9,
    page: 1,
    pageSize: 20,
};

export const AGENT_PAGE_TITLE_BY_PAGE_ID: Record<string, string> = Object.fromEntries(
    MOCK_AGENT_DOCK_FLYOUT_ITEMS_RESPONSE.items
        .filter((item) => !!item.pageId)
        .map((item) => [item.pageId!.toLowerCase(), item.name]),
);

export const MOCK_AGENT_DOCK_FLYOUT_FILTER_OPTIONS: DockFlyoutFilterOptionsResponse = {
    ownership: [
        { key: "shared", label: "Shared" },
        { key: "personal", label: "Personal" },
        { key: "global", label: "Global" },
        { key: "member-firm", label: "Member Firm" },
    ],
    category: [
        { key: "general-purpose", label: "General Purpose" },
        { key: "financial-statements", label: "Financial Statements" },
        { key: "knowledge", label: "Knowledge" },
    ],
};

const agentSortConfig: ItemSortConfig<AgentDockFlyoutItem> = {
    compareByField: (sortId) => {
        switch (sortId) {
            case "a-z":
                return (a, b) => a.name.localeCompare(b.name);
            case "z-a":
                return (a, b) => b.name.localeCompare(a.name);
            case "recently-created":
                return (a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            case "recently-used":
            default:
                return (a, b) =>
                    new Date(b.lastRun).getTime() - new Date(a.lastRun).getTime();
        }
    },
};

const agentQueryConfig: ItemQueryConfig<AgentDockFlyoutItem> = {
    searchableFields: (item) => [item.name, item.description, item.agentId],
};

const agentScopeConfig: ItemScopeConfig<AgentDockFlyoutItem> = {
    getScopeValue: (item) => item.ownership,
};

const agentFilterConfig: ItemFilterConfig<AgentDockFlyoutItem> = {
    matchesFilter: (item, filters) => {
        const selectedCategories = filters.category ?? [];

        if (
            selectedCategories.length > 0 &&
            !item.categories.some((category) => selectedCategories.includes(category))
        ) {
            return false;
        }

        return true;
    },
};

function mapAgentFilterSections(
    filterOptions: DockFlyoutFilterOptionsResponse,
): DockFlyoutFilterSection[] {
    return [
        {
            id: "sort",
            label: "Sort",
            type: "radio",
            options: [
                { id: "a-z", label: "A-Z" },
                { id: "z-a", label: "Z-A" },
                { id: "recently-used", label: "Recently Used" },
                { id: "recently-created", label: "Recently Created" },
            ],
            defaultValue: "recently-used",
        },
        {
            id: "category",
            label: "Category",
            type: "checkbox",
            includeAllOption: true,
            allOptionLabel: "All",
            options: filterOptions.category.map((option) => ({
                id: option.key,
                label: option.label,
            })),
            defaultValue: filterOptions.category.map((option) => option.key),
        },
    ];
}

function mapAgentItemToListItem(item: AgentDockFlyoutItem): DockFlyoutListItem {
    return {
        id: item.id,
        name: item.name,
        subtitle: item.description,
        appId: "kci-agents",
        pageId: item.pageId || `agent-${item.agentId}`,
        unread: item.activitySummary.actionRequired > 0,
        isAggregate: item.isAggregate ?? false,
        scopeId: item.ownership,
        lastUsedAt: item.lastRun,
        createdAt: item.createdAt,
        filters: {
            ownership: item.ownership,
            category: item.categories,
        },
        navigationMode: "direct",
        optionsMenu: {
            show: item.optionsMenu.show,
            actions: mapActionIdsToActions(item.optionsMenu.actions),
        },
    };
}

export async function loadAgentsDockFlyoutMockData(
    params: LoadDockFlyoutDataParams = {},
): Promise<DockFlyoutDataModel> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const query = params.query ?? "";
    const scope = params.scope ?? "all";
    const filters: DockFlyoutFiltersState = params.filters ?? {};
    const allItems = MOCK_AGENT_DOCK_FLYOUT_ITEMS_RESPONSE.items;

    const fixedTopItems = allItems.filter((item) => item.isAggregate);
    const regularItems = allItems.filter((item) => !item.isAggregate);

    const filteredItems = regularItems.filter((item) => {
        return (
            matchesQuery(item, query, agentQueryConfig) &&
            matchesScope(item, scope, agentScopeConfig) &&
            matchesFilters(item, filters, agentFilterConfig)
        );
    });

    const sortId = filters.sort?.[0];
    const sortedItems = sortItems(filteredItems, sortId, agentSortConfig);
    const pagedResult = paginateItems(sortedItems, page, pageSize);

    const mappedItems = [
        ...(page === 1 ? fixedTopItems.map(mapAgentItemToListItem) : []),
        ...pagedResult.items.map(mapAgentItemToListItem),
    ];

    return mockDelay(
        {
            title: "Agent Activity",
            scopes: buildOwnershipScopes(MOCK_AGENT_DOCK_FLYOUT_FILTER_OPTIONS.ownership, "All"),
            filterSections: mapAgentFilterSections(MOCK_AGENT_DOCK_FLYOUT_FILTER_OPTIONS),
            items: mappedItems,
            groups: [],
            pagination: {
                page,
                pageSize,
                total: pagedResult.total,
                hasMore: pagedResult.hasMore,
            },
        },
        200,
    );
}